import { useAtomValue, useSetAtom } from 'jotai';
import { resolveOtherInfoObj } from '@moko/utils';
import { useEffect, useRef, useState } from 'react';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createAsset,
  editAsset,
  getAsset,
  getAssetStatuses,
  IAsset,
  IAssetStatus,
  ICreateAssetRequest,
  IUpdateAssetRequest,
} from '../../services/assetServices';
import {
  getEntitiesMetadatas,
  getMetadata,
  IMetadata,
} from '../../services/metadataService';
import ZoneSelector from '../zones/ZoneSelector';
import { Stack } from '@fluentui/react';
import AssetSelector from '../works/AssetSelector';
import { EditItem } from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import { v4 as uuid } from 'uuid';
import AssetCategorySelector from './AssetCategorySelector';
import { FeatureFlag, Features } from '../../libs/featureAccess';
import { IValidationError } from '@moko/utils/src/interfaces/MetadataInterfaces';
import {
  hasCodeConventionFormat,
  tenantInfoAtom,
} from '../../atoms/tenantAtom';

interface IAssetDetailsV2Props {
  itemIdOverride?: number;
  backPath?: string;
  onCancel?: () => void;
  onAssetCreated?: (asset: {
    id: number;
    code: string;
    name: string;
    uuid: string;
  }) => void | Promise<void>;
}

const AssetDetailsV2 = ({
  itemIdOverride,
  backPath = '/assets',
  onCancel,
  onAssetCreated,
}: IAssetDetailsV2Props) => {
  const isSaved = useRef(false);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [assetStatuses, setAssetStatuses] = useState<IAssetStatus[]>([]);
  const [currentAssetId, setCurrentAssetId] = useState<number>();
  const [parentAssetId, setParentAssetId] = useState<number>();
  const [parentAssetName, setParentAssetName] = useState<string>();
  const [zoneId, setZoneId] = useState<number>();
  const [categoryId, setCategoryId] = useState<number>();

  const [metadata, setMetadata] = useState<IMetadata>();
  const [metadataName, setMetadataName] = useState<string>();
  const [metadatas, setMetadatas] = useState<{ key: string; value: string }[]>(
    []
  );
  const [otherInfo, setOtherInfo] = useState<Record<string, unknown>>({});

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);
      try {
        const assetMetadatas = await getEntitiesMetadatas(abortController, [
          'Asset',
        ]);
        const metadatas = Object.keys(assetMetadatas?.metadatas || {}).map(
          (key) => ({ key: key, value: key.split('|')[1] })
        );
        setMetadatas(metadatas ?? []);
        setAssetStatuses(await getAssetStatuses(abortController));
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
  }, []);

  const [errors, setErrors] = useState<IValidationError[]>();

  const tenantInfo = useAtomValue(tenantInfoAtom);
  const hasCodeConvention = hasCodeConventionFormat(
    tenantInfo,
    'asset-code-convention'
  );
  const isEditMode = !!currentAssetId;

  return (
    <Stack>
      <EditItem
        itemIdOverride={itemIdOverride}
        getAction={async (abortController, id) => {
          if (!id) {
            const defaultMetadata = await getMetadata(abortController, 'Asset');
            setCurrentAssetId(undefined);
            setZoneId(undefined);
            setParentAssetId(undefined);
            setParentAssetName(undefined);
            setCategoryId(undefined);
            setMetadataName(undefined);
            setMetadata(defaultMetadata);
            setOtherInfo({});
            return { otherInfo: '{}' };
          }

          const asset: any = await getAsset(abortController, id);
          const metadataName = asset.metadataName
            ? asset.metadataName.split('|')[1]
            : undefined;
          setMetadataName(metadataName);
          const metadata = await getMetadata(
            abortController,
            'Asset',
            undefined,
            metadataName
          );
          setMetadata(metadata);
          setCurrentAssetId(id);
          setZoneId(asset.zoneId);
          setParentAssetId(asset.parentId);
          setParentAssetName(asset.parentName);
          setCategoryId(asset.categoryId);
          asset.statusId = (asset.statusId ?? '').toString();
          setOtherInfo(resolveOtherInfoObj(asset.otherInfo) ?? {});
          return asset;
        }}
        newAction={async (abortController, newAsset) => {
          const createAssetRequest: ICreateAssetRequest = {
            uuid: uuid(),
            code: newAsset.code as string,
            name: newAsset.name as string,
            parentId: parentAssetId,
            categoryId: categoryId,
            zoneId: zoneId,
            statusId: Number.parseInt(newAsset.statusId as string),
            changedate: new Date().toISOString(),
            otherInfo,
            metadataName: metadataName ? `Asset|${metadataName}` : undefined,
          };
          const response = await createAsset(
            abortController,
            createAssetRequest
          );
          if (response.isSuccess) {
            setSuccessMessage(`Asset created successfully.`);
            setCurrentAssetId(response.assetId);
            if (onAssetCreated) {
              await onAssetCreated({
                id: response.assetId,
                code: createAssetRequest.code,
                name: createAssetRequest.name,
                uuid: createAssetRequest.uuid,
              });
            }
          } else {
            throw new Error(
              response.message === 'Dup code'
                ? `Asset with this code already exists.`
                : response.message === 'Exceed license'
                ? `Number of assets created exceed the license.`
                : response.message
            );
          }
        }}
        editAction={async (abortController, id, editedAsset) => {
          const updateAssetRequest: IUpdateAssetRequest = {
            code: editedAsset.code as string,
            name: editedAsset.name as string,
            parentId: parentAssetId,
            categoryId: categoryId,
            zoneId: zoneId,
            statusId: Number.parseInt(editedAsset.statusId as string),
            changedate: editedAsset.changeDate as string,
            metadataName: metadataName ? `Asset|${metadataName}` : undefined,
            otherInfo,
          };
          await editAsset(abortController, id, updateAssetRequest);
          setSuccessMessage(`Asset with id: ${id} successfully updated.`);
        }}
        isSaved={isSaved}
        back={backPath}
        onCancel={onCancel}
        callGetBeforeNew={true}
        afterChange={async (item: any) => {
          if (item.hasOwnProperty('metadataName')) {
            const newName = item.metadataName
              ? item.metadataName.split('|')[1]
              : undefined;
            if (newName !== metadataName) {
              setMetadataName(newName);
              const abortController = new AbortController();
              const md = await getMetadata(
                abortController,
                'Asset',
                undefined,
                newName
              );
              setMetadata(md);
            }
          }
        }}
        metadata={{
          fields: [
            ...(hasCodeConvention && !isEditMode
              ? []
              : [
                  {
                    name: 'code',
                    fieldType: 'String',
                    label: 'Code',
                    readonly: hasCodeConvention && isEditMode,
                  },
                ]),
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
            },
            {
              name: 'statusId',
              fieldType: 'Lookup',
              label: 'Status',
              lookupList: 'Statuses',
            },
            {
              name: 'metadataName',
              fieldType: 'Lookup',
              label: 'Template',
              lookupList: 'entitiesMetadatas',
              effect: currentAssetId ? 'Readonly' : 'Show',
            },
          ],
          lookups: [
            {
              name: 'Statuses',
              values: assetStatuses?.map((status) => ({
                key: status.id.toString(),
                value: status.name,
                icon: status.icon,
              })),
            },
            {
              name: 'entitiesMetadatas',
              values: metadatas,
            },
          ],
          validations: [],
          interactions: [],
        }}
      >
        <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: 350 } }}>
          <FeatureFlag featureKey={Features.FEATURE_CATEGORIES}>
            <AssetCategorySelector
              categoryId={categoryId}
              disabled={isSaved.current}
              onSelected={(id: number) => setCategoryId(id)}
            />
          </FeatureFlag>
          <FeatureFlag featureKey={Features.FEATURE_ZONES}>
            <ZoneSelector
              zoneId={zoneId}
              disabled={isSaved.current}
              onSelected={(id: number) => setZoneId(id)}
            />
          </FeatureFlag>
          <FeatureFlag featureKey={Features.FEATURE_ASSET_HIERARCHY}>
            <AssetSelector
              label="Parent"
              assetId={parentAssetId}
              assetName={parentAssetName}
              disabled={isSaved.current}
              onSelected={(asset: IAsset) => {
                setParentAssetId(asset.id);
                setParentAssetName(asset.name);
              }}
              selectorPanelHeaderText="Select Parent Asset"
            />
          </FeatureFlag>
          <p style={{ height: '2px' }} />
        </Stack>
      </EditItem>
      {metadata ? (
        <Stack tokens={{ childrenGap: 10 }}>
          <OtherInfo
            metadata={metadata}
            otherInfo={JSON.stringify(otherInfo)}
            isSaved={isSaved}
            onOtherInfoChanged={(newValue) =>
              setOtherInfo(newValue as Record<string, unknown>)
            }
            validationErrors={errors}
            onValidationErrorsChanged={(newErrors) => setErrors(newErrors)}
          />
        </Stack>
      ) : (
        <></>
      )}
    </Stack>
  );
};

export default AssetDetailsV2;
